import React, { useState, useEffect } from 'react';

interface TestMetadata {
  systemName: string;
  testerName: string;
  environment: string;
  startDate: string;
  version?: string;
  platforms?: ('iOS' | 'Android' | 'Web')[];
}

interface TestResult {
  metadata: TestMetadata;
}

interface NavbarProps {
  currentTest: TestResult | null;
  hasActiveTest: boolean;
  onBack: () => void;
  username?: string;
  onLogout: () => void;
  onVersionClick?: (version: string) => void;
  onLogoClick?: () => void;
  onLastTestClick?: () => void;
  isAnalyzing?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ 
  currentTest, 
  hasActiveTest,
  onBack,
  username,
  onLogout,
  onVersionClick,
  onLogoClick,
  onLastTestClick,
  isAnalyzing
}) => {
  const [lastTestId, setLastTestId] = useState<string | null>(null);
  const [lastTestData, setLastTestData] = useState<{ metadata: TestMetadata } | null>(null);

  useEffect(() => {
    const savedLastTest = localStorage.getItem('lastTestId');
    setLastTestId(savedLastTest);
  }, []);

  useEffect(() => {
    if (!currentTest && lastTestId) {
      const API_BASE_URL = process.env.REACT_APP_API_HOST;
      fetch(`${API_BASE_URL}/api/tests/test_results/${lastTestId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && data.metadata) {
            setLastTestData({ metadata: data.metadata });
          } else {
            setLastTestData(null);
          }
        })
        .catch(() => setLastTestData(null));
    } else {
      setLastTestData(null);
    }
  }, [currentTest, lastTestId]);

  const testToShow = currentTest || lastTestData;

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else if (isAnalyzing) {
      window.history.pushState({}, '', '/home');
      window.location.href = '/home';
    } else {
      window.history.pushState({}, '', '/home');
    }
  };

  return (
    <header className="fixed w-full top-0 z-50">
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 dark:from-slate-800 dark:to-slate-700 text-white shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div 
                className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleLogoClick}
              >
                <svg className="h-8 w-8 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-bold text-xl tracking-tight">TestManager</span>
              </div>
            </div>
            
            <div className="flex items-center">
              {username && (
                <div className="flex items-center mr-4">
                  <div className="flex items-center bg-blue-800 bg-opacity-30 rounded-lg px-3 py-1.5 text-xs md:text-sm">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{username}</span>
                  </div>
                  
                  {onLogout && (
                    <button 
                      onClick={onLogout}
                      className="ml-2 px-2 py-1 rounded-md text-xs font-medium bg-blue-800 hover:bg-blue-900 transition-colors duration-200"
                    >
                      <div className="flex items-center">
                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Wyloguj
                      </div>
                    </button>
                  )}
                </div>
              )}
              
              {testToShow ? (
                <button 
                  className="flex items-center bg-blue-800 bg-opacity-30 rounded-lg px-4 py-2 text-xs md:text-sm hover:bg-blue-700 transition-colors duration-200"
                  onClick={() => {
                    if (currentTest && currentTest.metadata.version) {
                      onVersionClick?.(currentTest.metadata.version);
                    } else if (lastTestId) {
                      onLastTestClick?.();
                    }
                  }}
                >
                  <div className="mr-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="font-semibold">{testToShow.metadata.systemName}</span>
                    </div>
                    <div className="flex items-center mt-1">
                      <svg className="w-3 h-3 mr-1 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-blue-200">
                        {new Date(testToShow.metadata.startDate).toLocaleDateString('pl-PL')}
                      </span>
                    </div>
                  </div>
                  <div className="border-l border-blue-400 pl-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-1 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-blue-100">{testToShow.metadata.testerName}</span>
                    </div>
                    <div className="flex items-center mt-1">
                      <svg className="w-3 h-3 mr-1 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span className="text-blue-200">{testToShow.metadata.environment}</span>
                    </div>
                    {testToShow.metadata.version && (
                      <div className="flex items-center mt-1">
                        <svg className="w-3 h-3 mr-1 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className="text-blue-200">v{testToShow.metadata.version}</span>
                      </div>
                    )}
                  </div>
                </button>
              ) : (
                <div className="text-xs md:text-sm text-white px-4 py-2">
                  <span className="font-medium">Wybierz scenariusz testowy</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-400 opacity-75"></div>
    </header>
  );
};

export default Navbar; 